import { makeAutoObservable, runInAction } from "mobx";
import agent from "../api/agent";
import {
  GetAllCitiesApiResponse,
  GetAllStateApiResponse,
  CustomerTypeResponse,
  BusinessTypeResponse,
} from "../models/Onboarding";

export default class OnboardingStore {
  stateList: GetAllStateApiResponse | null = null;
  cityList: GetAllCitiesApiResponse | null = null;
  customerTypeList: CustomerTypeResponse | null = null;
  businessTypeList: BusinessTypeResponse | null = null;
  subBusinessTypeList: BusinessTypeResponse | null = null;

  loadingState: boolean = false;
  loadingCity: boolean = false;
  loadingCustomerTypes: boolean = false;
  loadingBusinessTypes: boolean = false;
  isAddingOutlet: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  /*------------------ CRM Customers ------------------*/
  crmOnboardCustomers: any[] = [];
  loadingCrmCustomers: boolean = false;

  getCrmOnboardCustomers = async () => {
    this.loadingCrmCustomers = true;
    try {
      const result = await agent.Onboarding.getCrmOnboardCustomers();
      runInAction(() => {
        this.crmOnboardCustomers = result?.data || result;
        this.loadingCrmCustomers = false;
      });
    } catch (error) {
      console.error("Error fetching CRM customers:", error);
      runInAction(() => {
        this.loadingCrmCustomers = false;
      });
    }
  };

  /*------------------ State Loader ------------------*/
  getLoadingState = () => this.loadingState;

  setLoadingState = (val: boolean) => {
    this.loadingState = val;
  };

  getAllState = async () => {
    this.setLoadingState(true);
    try {
      const result = await agent.Onboarding.getAllState();
      runInAction(() => {
        this.stateList = result;
        this.loadingState = false;
      });
    } catch (error) {
      console.error("Error fetching states:", error);
      runInAction(() => {
        this.loadingState = false;
      });
    }
  };

  /*------------------ City Loader ------------------*/
  getLoadingCity = () => this.loadingCity;

  setLoadingCity = (val: boolean) => {
    this.loadingCity = val;
  };

  getAllCities = async (stateId: number) => {
    this.setLoadingCity(true);
    try {
      const result = await agent.Onboarding.getAllCities(stateId);
      runInAction(() => {
        this.cityList = result;
        this.loadingCity = false;
      });
    } catch (error) {
      console.error("Error fetching cities:", error);
      runInAction(() => {
        this.loadingCity = false;
      });
    }
  };

  /*------------------ Customer Types ------------------*/
  getLoadingCustomerTypes = () => this.loadingCustomerTypes;

  setLoadingCustomerTypes = (val: boolean) => {
    this.loadingCustomerTypes = val;
  };

  getCustomerTypes = async () => {
    this.setLoadingCustomerTypes(true);
    try {
      const result = await agent.Onboarding.getCustomerTypes();
      runInAction(() => {
        this.customerTypeList = result;
        this.loadingCustomerTypes = false;
      });
    } catch (error) {
      console.error("Error fetching customer types:", error);
      runInAction(() => {
        this.loadingCustomerTypes = false;
      });
    }
  };

  /*------------------ Business Types ------------------*/
  getLoadingBusinessTypes = () => this.loadingBusinessTypes;

  setLoadingBusinessTypes = (val: boolean) => {
    this.loadingBusinessTypes = val;
  };

  getBusinessTypes = async (addressType: string) => {
    this.setLoadingBusinessTypes(true);
    try {
      const result = await agent.Onboarding.getBusinessTypes(addressType);
      runInAction(() => {
        this.businessTypeList = result;
        this.loadingBusinessTypes = false;
      });
      return result;
    } catch (error) {
      runInAction(() => {
        this.loadingBusinessTypes = false;
      });
      console.error("Error fetching business types:", error);
      throw error;
    }
  };

  /*------------------ Sub Business Types ------------------*/
  getSubBusinessTypes = async (businessType: string) => {
    this.setLoadingBusinessTypes(true);
    try {
      const result = await agent.Onboarding.getBusinessTypes(businessType);
      runInAction(() => {
        this.subBusinessTypeList = result;
        this.loadingBusinessTypes = false;
      });
      return result;
    } catch (error) {
      runInAction(() => {
        this.loadingBusinessTypes = false;
      });
      console.error("Error fetching sub business types:", error);
      throw error;
    }
  };

  clearBusinessTypes = () => {
    this.businessTypeList = null;
    this.subBusinessTypeList = null;
  };

  clearSubBusinessTypes = () => {
    this.subBusinessTypeList = null;
  };

  /*------------------ Add Outlet ------------------*/
  setIsAddingOutlet = (val: boolean) => {
    this.isAddingOutlet = val;
  };

  addOutlet = async (data: any) => {
    this.setIsAddingOutlet(true);
    try {
      const result = await agent.Onboarding.addOutlet(data);
      runInAction(() => {
        this.isAddingOutlet = false;
      });
      return result;
    } catch (error) {
      runInAction(() => {
        this.isAddingOutlet = false;
      });
      console.error("Error adding customer outlet:", error);
      throw error;
    }
  };

  uploadAttachments = async (outletId: number, formData: FormData) => {
    try {
      const result = await agent.Onboarding.uploadAttachments(
        outletId,
        formData,
      );
      return result;
    } catch (error) {
      console.error("Error uploading attachments:", error);
      throw error;
    }
  };

  addOrganisation = async (data: any) => {
    try {
      const result = await agent.Onboarding.addOrganisation(data);
      return result;
    } catch (error) {
      console.error("Error adding organisation:", error);
      throw error;
    }
  };

  uploadOrganisationImage = async (formData: FormData) => {
    try {
      const result = await agent.Onboarding.uploadOrganisationImage(formData);
      return result;
    } catch (error) {
      console.error("Error uploading organisation image:", error);
      throw error;
    }
  };

  getOutletByOrgId = async (orgId: number) => {
    try {
      const result = await agent.Onboarding.getOutletByOrgId(orgId);
      return result;
    } catch (error) {
      console.error("Error fetching outlet by org id:", error);
      throw error;
    }
  };

  updateOutlet = async (outletId: number, data: any) => {
    this.setIsAddingOutlet(true);
    try {
      const result = await agent.Onboarding.updateOutlet(outletId, data);
      runInAction(() => {
        this.isAddingOutlet = false;
      });
      return result;
    } catch (error) {
      runInAction(() => {
        this.isAddingOutlet = false;
      });
      console.error("Error updating outlet:", error);
      throw error;
    }
  };
}
